import { KEYFRAME_ANIMATION } from '@/utils';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { Flex } from 'antd';
import { type FC, useEffect, useRef, useState } from 'react';
import { Box } from '../Box';
import { SortableItem } from './SortableItem';
import type { DnDColumnItem } from './types';

type Props = {
  id: 'displayed' | 'hidden';
  columns: DnDColumnItem[];
  onHide: (columnKey: string) => void;
  maxHeight?: number;
};

export const SortableContainer: FC<Props> = ({
  columns,
  id,
  onHide,
  maxHeight,
}) => {
  const preColumnKeys = useRef<string[] | null>(null);
  const ref = useRef<HTMLElement>(null);
  const [newColumnKey, setNewColumnKey] = useState<undefined | string>(
    undefined,
  );
  const { setNodeRef } = useDroppable({
    id,
  });

  useEffect(() => {
    const newColumnKeys = columns.map((column) => column.key);
    if (
      preColumnKeys.current &&
      preColumnKeys.current.length < newColumnKeys.length &&
      ref.current
    ) {
      const newColumnKey = newColumnKeys.at(-1);
      setNewColumnKey(newColumnKey);
      const bottom = ref.current.scrollHeight - ref.current.clientHeight;
      ref.current.scroll({ top: bottom, behavior: 'smooth' });
    }

    preColumnKeys.current = newColumnKeys;
  }, [columns]);

  return (
    <SortableContext id={id} items={columns.map((column) => column.title)}>
      <div ref={setNodeRef}>
        <Flex
          vertical
          ref={ref}
          gap={4}
          style={{ maxHeight, overflowY: 'auto' }}
        >
          {columns.map((column) => (
            <Box
              key={column.key}
              styleElement={KEYFRAME_ANIMATION.DEFAULT}
              animation={
                column.key === newColumnKey ? 'bg-color 2s ease' : undefined
              }
            >
              <SortableItem column={column} onHide={onHide} />
            </Box>
          ))}
        </Flex>
      </div>
    </SortableContext>
  );
};
